import { Pipe, PipeTransform, inject } from '@angular/core';
import {
  Activity,
  GroupedUserData,
  Shift,
  ShiftAssignment,
  UserTimelineModalShiftsAndAbsences,
  UserTimelinesModal,
  WithPreparedAttributes,
} from '@wilson/interfaces';
import { PriorityHelperService } from '@wilson/non-domain-specific/priority';
import { GanttOverlapHelperService } from '@wilson/shift-timeline/assignments-gantt-chart';
import { TimelineOverlapService } from '@wilson/shift-timeline/services';

interface TransformOptions {
  renderRadioInput: boolean;
  defaultRowHeight: number;
}

@Pipe({
  name: 'timelineModalOverlapLevels',
  standalone: true,
})
export class TimelineModalOverlapLevelsPipe implements PipeTransform {
  private timelineOverlapService = inject(TimelineOverlapService);
  private ganttOverlapHelperService = inject(GanttOverlapHelperService);
  private priorityHelperService = inject(PriorityHelperService);

  transform(
    userTimeline: UserTimelinesModal & {
      shiftAssignment?: ShiftAssignment | undefined;
    },
    options: TransformOptions,
  ): GroupedUserData[] {
    const shiftsWithPriority: (Shift &
      WithPreparedAttributes & {
        id: string;
        activities: Activity[];
        priority: number | null;
      })[] = userTimeline.shifts.map((shift) => {
      return {
        ...shift,
        priority: this.priorityHelperService.getHighestPriority(
          shift.activities,
        ),
      };
    });

    if (options.renderRadioInput && userTimeline.shiftAssignment) {
      const existingShiftIndex = shiftsWithPriority.findIndex(
        (shift) => shift?.id === userTimeline?.shiftAssignment?.shift?.id,
      );
      if (existingShiftIndex !== -1) {
        shiftsWithPriority.splice(existingShiftIndex, 1);
      }
      shiftsWithPriority.push(
        userTimeline.shiftAssignment.shift as Shift &
          WithPreparedAttributes & {
            id: string;
            activities: Activity[];
            priority: number | null;
          },
      );
    }

    const scheduleItems: UserTimelineModalShiftsAndAbsences[] = [
      ...shiftsWithPriority,
      ...userTimeline.absences,
    ];

    const userDataWithOverlapLevels =
      this.timelineOverlapService.determineOverlaps(scheduleItems);

    const itemsWithLevelsAndHeights =
      this.ganttOverlapHelperService.processUserDataWithOverlapLevels(
        userDataWithOverlapLevels,
        options.defaultRowHeight,
      );

    return itemsWithLevelsAndHeights;
  }
}
